import {Percent, Prose} from '@raison/types';
import React from 'react';
import {DashboardCategoryPortion} from '~/entities/dashboard-categories';
import {cn, RsnBublikChart, useBoolean} from '~/library/ui-kit';
import {useAccordion} from '../AccountGroupPage/AccountGroupItem/Accordion';
import * as css from './TotalBalance.css';

export function TotalBalanceToast({
    useBalance,
    portions,
}: {
    useBalance: () => Prose;
    portions: DashboardCategoryPortion[];
}) {
    const parts = portions
        .filter(p => p.categoryPercent > 0)
        .map(p => ({
            color: p.categoryColor,
            name: p.categoryName,
            percent: p.categoryPercent,
        }));
    const partsByPerecnt = parts.sort((a, b) => b.percent - a.percent);

    const count = parts.length;
    const canOpen = count > 0;
    const [isOpen, setOpen] = useBoolean();
    const accordion = useAccordion(isOpen, setOpen.toggle);
    const balance = useBalance();

    return (
        <details {...accordion.detailsProps} className='rounded-lg bg-neutral-10'>
            <summary {...accordion.summaryProps} className='box-content grid grid-cols-[3rem,1fr,auto] gap-3 p-4'>
                <RsnBublikChart parts={parts} gap={0.5} />
                <div className='flex flex-1 flex-col gap-1'>
                    <span className='fv-text-m-regular text-neutral-60'>{'Total balance'}</span>
                    <span className='fv-heading-l text-neutral-100'>{balance}</span>
                </div>
                {canOpen && (
                    <div className={`grid place-items-center transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                        <ArrowDown />
                    </div>
                )}
            </summary>
            {canOpen && (
                <ul {...accordion.contentProps} className={'grid grid-cols-2 gap-3 p-4 only:*:col-span-2'}>
                    {partsByPerecnt.map(part => (
                        <li
                            key={part.color}
                            className={cn('fv-text-m-regular flex gap-2', css.listStyle)}
                            style={assignCssVars({
                                [css.markerColor]: part.color,
                            })}
                        >
                            <div className='flex-1 text-neutral-100'>{part.name}</div>
                            <div className='text-neutral-60'>{Percent.format(part.percent)}</div>
                        </li>
                    ))}
                </ul>
            )}
        </details>
    );
}

function assignCssVars(vars: Record<string, string>) {
    return Object.fromEntries(Object.entries(vars).map(([cssVar, value]) => [cssVar.slice(4, -1), value]));
}

function ArrowDown() {
    return (
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='24' height='24'>
            <path fill='currentColor' d='m12 13.172 4.95-4.95 1.414 1.414L12 16 5.636 9.636 7.05 8.222z' />
        </svg>
    );
}
